/**
 * Selectors / predicates shared by every tour that targets the
 * Interactive Viewer page (``InteractiveViewerPage``). Both the In
 * Progress and Asbuilds tours render against the same DOM, so the
 * questions they ask ("is the panorama open?", "is the layout dropdown
 * showing?") are identical.
 */
import { has, waitForCondition } from '../utils/dom';

export const hasPhotoMarker = (): boolean => has('.photo-marker');

export const isListboxOpen = (): boolean => has('[role="listbox"]');

export const isPhotoTooltipOpen = (): boolean => has('.photo-selection-tooltip');

export const isPanoramaOpen = (): boolean =>
  has('[data-tour-id="iv-panorama-dialog"]');

export const isFabDialogOpen = (): boolean =>
  has('[data-tour-id="iv-panorama-fab-dialog"]');

export const isTabActive = (tabId: string): boolean =>
  has(`[data-tour-id="iv-tab-${tabId}"][aria-current="page"]`);

/**
 * Does the layout have at least one unit/area polygon drawn? When this is
 * true, photo markers are *hidden* until the user clicks a polygon — so
 * the tour has to walk the user through that interaction before it can
 * spotlight a marker.
 */
export const hasUnitPolygon = (): boolean =>
  has('[data-tour-id="iv-unit-polygon"]');

/**
 * Is the heat-map FAB currently visible? It renders on any loaded layout as
 * long as the user isn't in area-tag edit mode AND the heat map hasn't been
 * activated yet (the overlay is opt-in and dormant by default). Used as a
 * ``skipIf`` guard for the heat-map intro steps.
 */
export const isHeatMapToggleVisible = (): boolean =>
  has('[data-tour-id="iv-heatmap-toggle"]');

/**
 * Is the expanded heat-map legend panel currently mounted? This is only true
 * once the user (or the tour) has activated the heat map via the FAB.
 */
export const isHeatMapPanelOpen = (): boolean =>
  has('[data-tour-id="iv-heatmap-panel"]');

/**
 * Either the collapsed FAB or the expanded panel — i.e. the heat-map
 * feature is available on the current layout. A predicate over both so
 * tour authors don't have to care which UI mode the user landed in.
 */
export const isHeatMapAvailable = (): boolean =>
  isHeatMapToggleVisible() || isHeatMapPanelOpen();

/**
 * Wait until the layout's interactive content (markers OR polygons) has
 * finished rendering. With area-tag layouts, photo markers are gated
 * behind a polygon click, so polling for ``.photo-marker`` alone times
 * out and the tour skips half the steps.
 */
export const waitForLayoutMarkers = (timeoutMs = 4000) =>
  waitForCondition(() => hasPhotoMarker() || hasUnitPolygon(), timeoutMs);
